import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const MOVE_MIN_PX = 1;
/** Pixels traveled per millisecond. Higher is faster. */
export const CATALOG_MOVE_PX_PER_MS = 1;
/** Long hops never take longer than this. */
export const CATALOG_MOVE_MAX_MS = 1000;
/** Keep the previous catalog layout this long, then FLIP. Leaving tiles wait too. */
export const CATALOG_MOVE_DELAY_MS = 10;

const MOVE_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const catalogMoveDistance = (dx: number, dy: number): number => Math.hypot(dx, dy);

export const catalogMoveMs = (
  dx: number,
  dy: number,
  speed = CATALOG_MOVE_PX_PER_MS,
  maxMs = CATALOG_MOVE_MAX_MS
): number => (speed <= 0 ? 0 : Math.min(maxMs, catalogMoveDistance(dx, dy) / speed));

export const catalogMoveFinishMs = (durations: readonly number[]): number =>
  durations.reduce((max, ms) => Math.max(max, ms), 0);

export const catalogMoveTransition = (moveMs: number): string => `transform ${moveMs}ms ${MOVE_EASING}`;

export const catalogMoveTransform = (dx: number, dy: number): string => `translate3d(${dx}px, ${dy}px, 0)`;

const CATALOG_MOVE_NONE = { dx: 0, dy: 0 };

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const catalogSectionMoveDy = (fromTop: number | undefined, toTop: number): number =>
  fromTop == null ? 0 : fromTop - toTop;

export const catalogMoveDelta = (
  from: { left: number; top: number } | undefined,
  to: { left: number; top: number }
): { dx: number; dy: number } => ({
  dx: from == null ? 0 : from.left - to.left,
  dy: from == null ? 0 : from.top - to.top,
});

/** Read an in-flight translate so a new FLIP can start from the visible spot. */
export const catalogMoveTranslate = (transform: string): { dx: number; dy: number } => {
  if (!transform || transform === 'none') return { dx: 0, dy: 0 };
  const m3 = transform.match(/^matrix3d\((.+)\)$/);
  if (m3) {
    const n = m3[1].split(',').map(Number);
    return { dx: n[12] ?? 0, dy: n[13] ?? 0 };
  }
  const m = transform.match(/^matrix\((.+)\)$/);
  if (m) {
    const n = m[1].split(',').map(Number);
    return { dx: n[4] ?? 0, dy: n[5] ?? 0 };
  }
  return { dx: 0, dy: 0 };
};

export const catalogMoveFromBox = (
  prev: { left: number; top: number } | undefined,
  layout: { left: number; top: number },
  translate: { dx: number; dy: number }
): { left: number; top: number } => (prev ? { left: prev.left + translate.dx, top: prev.top + translate.dy } : layout);

/** Expand/collapse remasures only. A reorder uses those last boxes as the FLIP origin. */
export const catalogMoveShouldPlay = (prevOrderKey: string | null, orderKey: string): boolean =>
  prevOrderKey != null && prevOrderKey !== orderKey;

/** First paint and same-key updates stay live. A reorder waits `delayMs`. */
export const catalogMoveShouldDelay = (
  prevOrderKey: string | null,
  orderKey: string,
  delayMs = CATALOG_MOVE_DELAY_MS
): boolean => delayMs > 0 && Boolean(prevOrderKey) && prevOrderKey !== orderKey;

export const catalogScrollParent = (root: HTMLElement | null): HTMLElement | null =>
  root?.closest('.app-shell-main') ?? null;

export const applyCatalogScrollPin = (scroller: { scrollTop: number } | null, top: number | null): void => {
  if (scroller == null || top == null) return;
  scroller.scrollTop = top;
};

export const useDelayedCatalogOrder = <T>(live: T, orderKey: string, onDelay?: () => void): T => {
  const [shown, setShown] = useState(live);
  const [shownKey, setShownKey] = useState<string | null>(null);
  const liveRef = useRef(live);
  const onDelayRef = useRef(onDelay);
  liveRef.current = live;
  onDelayRef.current = onDelay;
  const delay = catalogMoveShouldDelay(shownKey, orderKey) && !prefersReducedMotion();

  useEffect(() => {
    if (!delay) {
      setShown(liveRef.current);
      setShownKey(orderKey);
      return;
    }
    onDelayRef.current?.();
    const timer = window.setTimeout(() => {
      setShown(liveRef.current);
      setShownKey(orderKey);
    }, CATALOG_MOVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [delay, orderKey]);

  return delay ? shown : live;
};

export const useCatalogSectionMove = (
  rootRef: { current: HTMLElement | null },
  orderKey: string,
  layoutKey = '',
  scrollTopRef?: { current: number | null }
) => {
  const boxes = useRef(new Map<string, { left: number; top: number }>());
  const prevOrderKey = useRef<string | null>(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const playMove = catalogMoveShouldPlay(prevOrderKey.current, orderKey) && !prefersReducedMotion();
    prevOrderKey.current = orderKey;
    if (playMove) {
      applyCatalogScrollPin(catalogScrollParent(root), scrollTopRef?.current ?? null);
      if (scrollTopRef) scrollTopRef.current = null;
    }
    const nextBoxes = new Map<string, { left: number; top: number }>();
    const moving: { node: HTMLElement; dx: number; dy: number; ms: number }[] = [];
    const rest: HTMLElement[] = [];
    for (const node of root.querySelectorAll<HTMLElement>('[data-bb-catalog], [data-bb-catalog-badge]')) {
      const id = node.getAttribute('data-bb-catalog-badge') || node.getAttribute('data-bb-catalog');
      if (!id) continue;
      const box = { left: node.offsetLeft, top: node.offsetTop };
      nextBoxes.set(id, box);
      if (!playMove) continue;
      const wasMoving = node.classList.contains('is-moving');
      const from = catalogMoveFromBox(
        boxes.current.get(id),
        box,
        wasMoving ? catalogMoveTranslate(getComputedStyle(node).transform) : CATALOG_MOVE_NONE
      );
      const { dx, dy } = catalogMoveDelta(from, box);
      if (Math.abs(dx) >= MOVE_MIN_PX || Math.abs(dy) >= MOVE_MIN_PX) {
        moving.push({ node, dx, dy, ms: catalogMoveMs(dx, dy) });
      } else if (wasMoving) {
        rest.push(node);
      }
    }
    boxes.current = nextBoxes;
    if (!playMove) return;
    for (const node of rest) {
      node.classList.remove('is-moving');
      node.style.transition = '';
      node.style.transform = '';
    }
    for (const item of moving) {
      item.node.classList.add('is-moving');
      item.node.style.transition = 'none';
      item.node.style.transform = catalogMoveTransform(item.dx, item.dy);
    }
    if (!moving.length) return;
    void root.offsetHeight;
    const play = () => {
      for (const { node, ms } of moving) {
        node.style.transition = catalogMoveTransition(ms);
        node.style.transform = '';
      }
    };
    const frame = requestAnimationFrame(play);
    const finish = window.setTimeout(
      () => {
        for (const { node } of moving) {
          node.classList.remove('is-moving');
          node.style.transition = '';
        }
      },
      catalogMoveFinishMs(moving.map((item) => item.ms))
    );
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(finish);
    };
  }, [orderKey, layoutKey, rootRef, scrollTopRef]);
};
